import { CheckCircleOutlined } from "@ant-design/icons";
import { Button, Divider, Form, Input, Space, Typography } from "antd";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmPassword from "@/components/Auth/Fields/ConfirmPassword";
import Password from "@/components/Auth/Fields/Password";
import { useGetCurrentUser } from "@/entities/current-user";
import { useAppNotification } from "@/modules/app-notification";

const { useForm } = Form;
const { Title } = Typography;
const { Paragraph } = Typography;

const TabPassword = () => {
  const [form] = useForm();
  const { t } = useTranslation();
  const { notify } = useAppNotification();
  const [codeSent, setCodeSent] = useState(false);
  const [isSendingNewCode, setIsSendingNewCode] = useState(false);
  const { currentUser } = useGetCurrentUser();

  const handleResetPassword = async () => {
    try {
      setIsSendingNewCode(true);
      const username = currentUser?.email;
      const output = await resetPassword({ username });
      handleResetPasswordNextSteps(output);
    } catch (_error) {
      setIsSendingNewCode(false);
      setCodeSent(false);
      notify.error({ title: t("user:preferences.changePassword.error") });
    }
  };

  const handleResetPasswordNextSteps = (output) => {
    const { nextStep } = output;

    switch (nextStep.resetPasswordStep) {
      case "CONFIRM_RESET_PASSWORD_WITH_CODE":
        notify.success({ title: t("user:preferences.resetCode.success") });
        setIsSendingNewCode(false);
        setCodeSent(true);
        break;
      case "DONE":
        notify.success({ title: t("user:preferences.changePassword.error") });
        break;
    }
  };

  const handleConfirmResetPassword = async () => {
    try {
      const username = currentUser?.email;
      await confirmResetPassword({
        username,
        confirmationCode: form.getFieldValue("confirmationCode"),
        newPassword: form.getFieldValue("newPassword"),
      });
      notify.success({ title: t("user:preferences.changePassword.success") });
    } catch (_error) {
      notify.error({ title: t("user:preferences.changePassword.error") });
    }
  };

  const FORM_ID = "updatePasswordForm";

  return (
    <Form
      id={FORM_ID}
      form={form}
      layout="vertical"
      onFinish={handleConfirmResetPassword}
    >
      <>
        <Title level={3}>{t("user:preferences.resetCode.title")}</Title>
        <Paragraph>{t("user:preferences.resetCode.info")}</Paragraph>
        <Button
          loading={isSendingNewCode}
          icon={codeSent ? <CheckCircleOutlined /> : undefined}
          onClick={handleResetPassword}
        >
          {t("global:send")}
        </Button>
        <Divider />
      </>
      <>
        <Title level={3}>{t("user:preferences.changePassword.title")}</Title>
        <Form.Item
          name="confirmationCode"
          label={t("user:form.resetCode.label")}
          rules={[{ required: true }]}
        >
          <Input
            placeholder={t("user:form.resetCode.label")}
            rules={[{ required: true }]}
          />
        </Form.Item>
        <Password
          name="newPassword"
          label={t("user:form.newPassword.label")}
          enforceStrongPassword
        />
        <ConfirmPassword
          name="newPasswordConfirmation"
          label={t("user:form.newPasswordConfirmation.label")}
          confirmField="newPassword"
        />
      </>
      <Space>
        <Button
          loading={false}
          data-testid="updatePassword"
          type="primary"
          htmlType="submit"
          form={FORM_ID}
        >
          {t("global:update")}
        </Button>
      </Space>
    </Form>
  );
};

export default TabPassword;
